import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import type { DayPlanQuery, DayPlanStatus, DayPlanVisitDto } from '@ctms/contracts';
import { isReadyForRegistration } from '@ctms/shared';
import { CalendarDays, Search } from 'lucide-react';

import {
  VISIT_FLAG_LABELS,
  VISIT_TYPE_LABELS,
} from '../../patients/patient-panel/chart-participation.js';
import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { useTrialRandomization } from '../../trials/use-trial.js';
import { visitsApi } from '../visits.api.js';
import { visitQueryKeys } from '../visits.query-keys.js';

export interface DayPlanProps {
  /** Active trial of the cockpit. `null` means "no trial picked yet" — then no request is sent. */
  trialId: number | null;
  /** `visits:read` (REQ-VIS-002) — without it the panel stays hidden. */
  canRead: boolean;
  /**
   * `visits:write` — the action column of REQ-VIS-017 stays hidden without it.
   */
  canWrite?: boolean;
  /**
   * REQ-VIS-017/REQ-VIS-030: opens the registration of the picked visit
   * (procedures + kits). A visit qualifies when it is not registered yet and
   * its booked day is today or earlier — the shared rule decides, not the view.
   */
  onRegisterVisit?: (visit: DayPlanVisitDto) => void;
  /**
   * Opens the kit registration dialog for the picked visit (REQ-KIT-014..026).
   */
  onRegisterKits?: (visit: DayPlanVisitDto) => void;
  /**
   * Opens the VCL print dialog for the picked visit (REQ-DOC-007, Domain 7).
   * Offered only with `documents:read` — the caller decides, the button follows.
   */
  onPrintVcl?: (visit: DayPlanVisitDto) => void;
  /**
   * Opens the patient todo card dialog for the picked visit (REQ-DOC-017,
   * Domain 7). Offered only with `documents:read` — the caller decides, the
   * button follows.
   */
  onPrintPatientTodo?: (visit: DayPlanVisitDto) => void;
}

const LIST_LIMIT = 25;

const columnHelper = createAppColumnHelper<DayPlanVisitDto>();

/**
 * Polish labels of the DERIVED day-plan status (REQ-VIS-035): a visit without
 * a completion date is planned, with one — registered. `patient_visits` stores
 * no status column, so this map is the only presentation of that rule.
 */
export const DAY_PLAN_STATUS_LABELS: Record<DayPlanVisitDto['status'], string> = {
  planned: 'Zaplanowana',
  registered: 'Zarejestrowana',
};

/** Local calendar day of "today" as `YYYY-MM-DD` — the default day of the plan. */
export function todayIso(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function patientName(row: DayPlanVisitDto): string {
  return `${row.patientFirstName} ${row.patientLastName}`;
}

/**
 * Patient name + enrollment numbers shown in the "Pacjent" column. The
 * randomization number joins only when the trial card keeps it (B.1/B.2).
 */
function patientLabel(row: DayPlanVisitDto, randomizationEnabled: boolean): JSX.Element {
  const randomizationNumber = randomizationEnabled ? row.randomizationNumber : null;
  return (
    <span className="flex items-baseline gap-1.5">
      <span>{patientName(row)}</span>
      <span className="text-[10px] text-slate-400 font-mono">
        {row.screeningNumber}
        {randomizationNumber ? ` / ${randomizationNumber}` : ''}
      </span>
    </span>
  );
}

/** Debounce delay for the search input (ms). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Element 1 of the Cockpit legend — the plan of one day of the active trial
 * (REQ-VIS-002). Columns follow the wireframe `main-panel.ui.md`:
 * `Godz. | Pacjent | Wizyta | Typ | Status | Flaga kitów`.
 *
 * The "Godz." column stays EMPTY: the hour of a visit is not in scope yet
 * (`Q-VIS`, `date_scheduled`/`date_real` are `date`, not timestamps).
 */
export function DayPlan({
  trialId,
  canRead,
  canWrite = false,
  onRegisterVisit,
  onRegisterKits,
  onPrintVcl,
  onPrintPatientTodo,
}: DayPlanProps): JSX.Element {
  const [date, setDate] = useState<string>(todayIso);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DayPlanStatus | ''>('');
  // B.1/B.2: the trial card decides whether a randomization number exists at all.
  const randomizationEnabled = useTrialRandomization(trialId);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAppliedSearch = useRef('');

  // Debounce the search input to avoid hammering the API on every keystroke.
  // Re-applying the same term is a no-op: the mount run (empty `search`) would
  // otherwise fire `setPage(1)` ~300 ms after the panel opens and snap a page
  // change made inside that window back to page 1.
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      const next = search.trim();
      if (next === lastAppliedSearch.current) {
        return;
      }
      lastAppliedSearch.current = next;
      setDebouncedSearch(next);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search]);

  const dayPlanQuery: DayPlanQuery = useMemo(
    () => {
      const q: DayPlanQuery = { trialId: trialId ?? 0, date, page, limit: LIST_LIMIT };
      if (statusFilter) {
        q.status = statusFilter;
      }
      if (debouncedSearch) {
        q.search = debouncedSearch;
      }
      return q;
    },
    [trialId, date, page, statusFilter, debouncedSearch],
  );

  const planQuery = useQuery({
    queryKey: visitQueryKeys.dayPlan(dayPlanQuery),
    queryFn: () => visitsApi.listDayPlan(dayPlanQuery),
    enabled: canRead && trialId !== null,
    // A day plan is operational, live data: a stale plan misleads the coordinator.
    staleTime: 0,
    retry: queryRetry,
  });

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: 'time',
          header: 'Godz.',
          cell: (info) => (
            <span className="font-mono text-xs text-slate-300">
              {info.row.original.time ?? '—'}
            </span>
          ),
        }),
        columnHelper.display({
          id: 'patient',
          header: 'Pacjent',
          cell: (info) => patientLabel(info.row.original, randomizationEnabled),
        }),
        columnHelper.display({
          id: 'visit',
          header: 'Wizyta',
          cell: (info) => {
            const row = info.row.original;
            return (
              <span className="flex items-baseline gap-1.5">
                {/* REQ-PAT-024: the schedule points at the protocol catalog, so it renders as `V<n>`. */}
                <span className="font-medium text-slate-100">{`V${row.visitNumber}`}</span>
                <span className="text-slate-400">{row.visitName}</span>
              </span>
            );
          },
        }),
        columnHelper.display({
          id: 'type',
          header: 'Typ',
          cell: (info) => VISIT_TYPE_LABELS[info.row.original.type],
        }),
        columnHelper.display({
          id: 'status',
          header: 'Status',
          cell: (info) => DAY_PLAN_STATUS_LABELS[info.row.original.status],
        }),
        columnHelper.display({
          id: 'kitFlag',
          header: 'Flaga kitów',
          cell: (info) => {
            const row = info.row.original;
            const flag = row.flag;
            const label = flag === null ? '—' : VISIT_FLAG_LABELS[flag];
            if (onRegisterKits) {
              return (
                <button
                  type="button"
                  onClick={() => onRegisterKits(row)}
                  title={`Zarządzaj kitami dla wizyty V${row.visitNumber}`}
                  className="rounded px-1 py-0.5 hover:bg-slate-800 hover:text-cyan-300 transition-colors"
                >
                  {label}
                </button>
              );
            }
            return label;
          },
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Akcje',
          cell: (info) => {
            const row = info.row.original;
            // REQ-VIS-017: only a scheduled visit whose booked day is today or
            // earlier qualifies; the shared rule decides, not the view.
            const ready = isReadyForRegistration(
              { scheduledDate: row.date, scheduledTime: null },
              { performedDate: row.status === 'registered' ? row.date : null, performedTime: null },
              todayIso(),
            );
            const showRegisterVisit = canWrite && ready && onRegisterVisit;
            const showRegisterKits = Boolean(onRegisterKits);
            const showPrintVcl = Boolean(onPrintVcl);
            const showPrintPatientTodo = Boolean(onPrintPatientTodo);

            if (!showRegisterVisit && !showRegisterKits && !showPrintVcl && !showPrintPatientTodo) {
              return <span className="text-[11px] text-slate-500">—</span>;
            }
            return (
              <div className="flex items-center gap-1.5">
                {showRegisterVisit && (
                  <button
                    type="button"
                    onClick={() => onRegisterVisit(row)}
                    title={`Rejestruj wizytę ${row.patientFirstName} ${row.patientLastName}`}
                    className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2 py-1 text-[11px] font-medium text-cyan-300 hover:bg-cyan-900/60"
                  >
                    REJESTRUJ
                  </button>
                )}
                {showRegisterKits && (
                  <button
                    type="button"
                    onClick={() => onRegisterKits?.(row)}
                    title={`Rejestruj kity dla wizyty V${row.visitNumber}`}
                    className="rounded border border-emerald-700/70 bg-emerald-950/40 px-2 py-1 text-[11px] font-medium text-emerald-300 hover:bg-emerald-900/60"
                  >
                    KITY
                  </button>
                )}
                {showPrintVcl && (
                  <button
                    type="button"
                    onClick={() => onPrintVcl?.(row)}
                    title={`Check-lista VCL dla wizyty V${row.visitNumber}`}
                    className="rounded border border-amber-700/70 bg-amber-950/40 px-2 py-1 text-[11px] font-medium text-amber-300 hover:bg-amber-900/60"
                  >
                    VCL
                  </button>
                )}
                {showPrintPatientTodo && (
                  <button
                    type="button"
                    onClick={() => onPrintPatientTodo?.(row)}
                    title={`Karta zaleceń dla wizyty V${row.visitNumber}`}
                    className="rounded border border-violet-700/70 bg-violet-950/40 px-2 py-1 text-[11px] font-medium text-violet-300 hover:bg-violet-900/60"
                  >
                    Karta zaleceń
                  </button>
                )}
              </div>
            );
          },
        }),
      ]),
    [canWrite, onRegisterVisit, onRegisterKits, onPrintVcl, onPrintPatientTodo, randomizationEnabled],
  );

  const items = planQuery.data?.items ?? [];
  const table = useAppTable({ data: items, columns });

  if (!canRead) {
    return (
      <section role="alert" className="rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h2 className="text-base font-semibold text-slate-100">Brak dostępu do planu dnia</h2>
        <p className="mt-2 text-xs text-slate-400">
          Nie masz uprawnień do przeglądania planu dnia tego ośrodka. Jeżeli uważasz, że to błąd,
          skontaktuj się z administratorem ośrodka.
        </p>
      </section>
    );
  }

  if (trialId === null) {
    return (
      <section
        aria-label="Plan dnia"
        className="rounded-lg border border-slate-800 bg-slate-900/60 p-8"
      >
        <h2 className="text-sm font-semibold text-slate-100">Plan dnia</h2>
        <p className="mt-2 text-xs text-slate-400">Wybierz badanie, aby zobaczyć plan dnia.</p>
      </section>
    );
  }

  const retry = (): void => {
    void planQuery.refetch();
  };

  return (
    <section aria-label="Plan dnia" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
          <CalendarDays className="h-4 w-4 text-cyan-400" />
          Plan dnia
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Szukaj pacjenta…"
              aria-label="Szukaj pacjenta"
              className="rounded-md border border-slate-700 bg-slate-950 pl-7 pr-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 w-52 scheme-dark"
            />
          </div>
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as DayPlanStatus | '');
              setPage(1);
            }}
            aria-label="Filtruj wg statusu"
            className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark"
          >
            <option value="">Wszystkie statusy</option>
            <option value="planned">Zaplanowana</option>
            <option value="registered">Zarejestrowana</option>
          </select>
          {/* Date picker */}
          <label className="flex items-center gap-2 text-xs text-slate-400">
            Dzień planu
            <input
              type="date"
              value={date}
              aria-label="Wybierz dzień planu"
              onChange={(event) => {
                setDate(event.target.value);
                setPage(1);
              }}
              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark"
            />
          </label>
        </div>
      </div>

      {planQuery.isLoading ? (
        <p className="py-8 text-center text-xs text-slate-400">Ładowanie planu dnia</p>
      ) : planQuery.isError ? (
        <section role="alert" className="rounded-lg border border-rose-800/70 bg-rose-950/40 p-4">
          <p className="text-xs text-rose-200">Nie udało się wczytać planu dnia.</p>
          <button
            type="button"
            onClick={retry}
            className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </section>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 text-center">
          <p className="text-xs text-slate-400 font-medium">Brak wizyt w planie dnia</p>
          <p className="mt-1 text-[11px] text-slate-500">
            Wizyty pojawiają się po ustaleniu terminu. Wybierz pacjenta poniżej i użyj przycisku „Umawiaj” w jego harmonogramie.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/80">
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!planQuery.isLoading &&
      !planQuery.isError &&
      planQuery.data &&
      (planQuery.data.page > 1 || planQuery.data.hasNext) ? (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={planQuery.data.page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-40"
          >
            Poprzednia strona
          </button>
          <button
            type="button"
            disabled={!planQuery.data.hasNext}
            onClick={() => setPage((current) => current + 1)}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-40"
          >
            Następna strona
          </button>
        </div>
      ) : null}
    </section>
  );
}